import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

import { TenantAccount } from "~/api/Api";
import HostnamesList from "~/components/settings/HostnamesList";
import { useTeam } from "~/contexts/TeamProvider";
import { getLogoPlaceholderUrl } from "~/helpers/logoPlaceholder";

import labels from "../../helpers/labels";
import FormLabel from "../login/formLabel/FormLabel";
import Input from "../ui/input/Input";
import styles from "./Settings.module.scss";
import UploadImage from "./uploadImage/UploadImageControlled";

type TeamTenantType = {
  account: TenantAccount | null;
  canCreate: boolean;
  canUpdate: boolean;
};

export const TeamTenant = ({
  account,
  canCreate,
  canUpdate,
}: TeamTenantType) => {
  const { team } = useTeam();
  const {
    register,
    formState,
    formState: { errors },
    setValue,
    watch,
    resetField,
  } = useFormContext();

  const editable = Boolean(account ? canUpdate : canCreate);
  const wlLogoSignedOrUrl = watch("wl_logo");

  const [wlLogoPreviewUrl, setWlLogoPreviewUrl] = useState<string | null>(null);

  // if the form is no longer "dirty" (after reset/save) - remove local preview
  useEffect(() => {
    if (!formState.isDirty && wlLogoPreviewUrl) {
      URL.revokeObjectURL(wlLogoPreviewUrl);
      setWlLogoPreviewUrl(null);
    }
  }, [formState.isDirty, wlLogoPreviewUrl]);

  const serverUrl = useMemo(() => {
    // use form value if it's defined (even if it's null)
    const v =
      typeof wlLogoSignedOrUrl !== "undefined"
        ? wlLogoSignedOrUrl
        : (account?.logo ?? null);

    if (typeof v === "string" && /^https?:\/\//.test(v)) return v;
    if (
      v &&
      typeof v === "object" &&
      typeof v.url === "string" &&
      /^https?:\/\//.test(v.url)
    )
      return v.url;

    return null;
  }, [wlLogoSignedOrUrl, account?.logo]);

  // final URL for uploader: local preview is a priority
  const valueUrl = wlLogoPreviewUrl ?? serverUrl ?? null;

  // cleanup preview on unMount/changes
  useEffect(() => {
    return () => {
      if (wlLogoPreviewUrl) URL.revokeObjectURL(wlLogoPreviewUrl);
    };
  }, [wlLogoPreviewUrl]);

  const onUpload = (file: File) => {
    if (wlLogoPreviewUrl) URL.revokeObjectURL(wlLogoPreviewUrl);
    const next = URL.createObjectURL(file);
    setWlLogoPreviewUrl(next);

    setValue("wl_logoFile", file, { shouldDirty: true });
  };

  const onRemove = () => {
    if (wlLogoPreviewUrl) URL.revokeObjectURL(wlLogoPreviewUrl);
    setWlLogoPreviewUrl(null);

    setValue("wl_logo", null, { shouldDirty: true, shouldValidate: true });
    resetField("wl_logoFile", { defaultValue: null });
  };

  return (
    // @ts-expect-error legacy BEM classnames present only in CSS
    <div className={styles.settingsSectionGroup}>
      <p className={styles.settingsText}>
        Update general information about the white-label, set preferred
        configurations.
      </p>

      <div className={styles.settingsTeamForm}>
        <div className={styles.teamFormRow}>
          <FormLabel label="Logo" />
          <p className={styles.settingsText}>
            This image will be used while generating the reports and invoices,
            so consider not to leave it blank. Accepted formats: PNG, GIF, JPG,
            JPEG, WebP (max 5MB)
          </p>
          <div className={styles.settingsUpload}>
            <UploadImage
              disabled={!editable}
              valueUrl={valueUrl}
              placeholderLetter={(account?.name?.[0] || "W").toUpperCase()}
              placeholderImageUrl={getLogoPlaceholderUrl(team)}
              onUpload={onUpload}
              onRemove={onRemove}
            />
          </div>
        </div>

        <div className={styles.teamFormRow}>
          <FormLabel label="Name" />
          <Input
            disabled={!editable}
            errormessage={
              errors.wl_name
                ? errors.wl_name?.message?.toString() ||
                  labels.THIS_FIELD_CANNOT_BE_EMPTY
                : ""
            }
            {...register("wl_name", { required: true })}
            placeholder="Enter white-label name"
          />
        </div>

        <div className={styles.teamFormRow}>
          <FormLabel label="Support Email Address" />
          <Input
            disabled={!editable}
            errormessage={
              errors.wl_support_email
                ? errors.wl_support_email?.message?.toString() ||
                  labels.THIS_FIELD_CANNOT_BE_EMPTY
                : ""
            }
            {...register("wl_support_email", {
              required: false,
              setValueAs: (v) =>
                typeof v === "string" && v.trim() === "" ? null : v,
            })}
            placeholder="Enter email address for support requests"
          />
        </div>

        <div className={styles.teamFormRow}>
          <FormLabel label="Legal Address" />
          <Input
            disabled={!editable}
            errormessage={
              errors.wl_address
                ? errors.wl_address?.message?.toString() ||
                  labels.THIS_FIELD_CANNOT_BE_EMPTY
                : ""
            }
            {...register("wl_address", { required: false })}
            placeholder="Enter your legal address"
          />
        </div>
      </div>

      {account && <HostnamesList account={account} />}
    </div>
  );
};
